import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { errorMessage } from '../../errors';
import { toast } from '../../../hooks/use-toast';
import { uc } from '../../unifiedContactsApi';
import { confirmDialog } from '@/components/dialogs/DialogHost';
import { bakeoffYearOptions } from '../constants';

// Ticket 215: state for the Form 1084 / 91 / 92 worksheets. The backend owns every
// number; this hook only holds what the LO typed, builds the payloads and calls
// the endpoints. Labels, signs and hints come from /income/forms/definitions.
export const FORM_IDS = ['1084', '91', '92'];

let uidSeq = 0;
const nextUid = () => { uidSeq += 1; return `u${uidSeq}`; };

const latestYear = () => Number(bakeoffYearOptions()[0].value);

const urlLinks = () => {
  const p = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  return { contact_id: p.get('contact_id') || null, loan_file_id: p.get('loan_file_id') || null };
};

const blankLines = () => ({});
export const newYearSlot = (tax_year) => ({ tax_year, months: 12, lines: blankLines() });

export const newDraft = (formId) => {
  const meta = { borrower_name: '', ...urlLinks() };
  if (formId === '92') return { ...meta, properties: [] };
  return { ...meta, yearCount: 1, recentYear: latestYear(), method: null, sections: [] };
};

export const newProperty = (category) => ({
  uid: nextUid(), category, address: '', yearCount: 1, monthly_payment: null,
  years: [newYearSlot(latestYear()), newYearSlot(latestYear() - 1)],
});

export const newSection = (type) => ({
  uid: nextUid(), type, name: '', values: {}, ownership: {}, owns100: false,
});

const blankForm = (formId) => ({ draft: newDraft(formId), result: null, worksheet: null, dirty: false, busy: false });

// ---- payloads ---------------------------------------------------------------
const orNull = (v) => (v == null ? null : v);

export const cashFlowYears = (d) => (d.yearCount === 2 ? [d.recentYear - 1, d.recentYear] : [d.recentYear]);
const slotsFor = (d) => (d.yearCount === 2 ? ['prior', 'recent'] : ['recent']);

export function buildCashFlowPayload(def, d) {
  const slots = slotsFor(d);
  const payload = {
    years: cashFlowYears(d),
    sections: d.sections.map((s) => {
      const sdef = def.sections.find((x) => x.type === s.type);
      const values = {};
      sdef.lines.forEach((l) => {
        values[l.key] = slots.map((sl) => orNull(s.values[l.key]?.[sl]));
      });
      const out = { type: s.type, values };
      if (sdef.named && s.name.trim()) out.name = s.name.trim();
      if (sdef.ownership === 'percent') out.ownership_pct = slots.map((sl) => orNull(s.ownership[sl]));
      if (sdef.ownership === 'hundred_checkbox') out.owns_100_percent = !!s.owns100;
      return out;
    }),
  };
  if (d.borrower_name.trim()) payload.borrower_name = d.borrower_name.trim();
  if (d.method) payload.method = d.method;
  return payload;
}

export function buildRentalPayload(def, d) {
  const payment = (cat) => def.categories.find((c) => c.key === cat)?.needs_monthly_payment;
  const payload = {
    properties: d.properties.map((p) => {
      const years = p.years.slice(0, p.yearCount)
        .map((y) => {
          const row = { tax_year: Number(y.tax_year), months: orNull(y.months) };
          def.lines.forEach((l) => { row[l.key] = orNull(y.lines[l.key]); });
          return row;
        })
        .sort((a, b) => a.tax_year - b.tax_year); // the backend wants oldest first
      const out = { category: p.category, address: p.address.trim(), years };
      if (payment(p.category)) out.monthly_payment = orNull(p.monthly_payment);
      return out;
    }),
  };
  if (d.borrower_name.trim()) payload.borrower_name = d.borrower_name.trim();
  return payload;
}

export const buildPayload = (formId, def, draft) => (
  formId === '92' ? buildRentalPayload(def, draft) : buildCashFlowPayload(def, draft)
);

// ---- saved input -> draft ---------------------------------------------------
function draftFromInput(formId, row) {
  const input = row.input || {};
  const meta = {
    borrower_name: row.borrower_name || input.borrower_name || '',
    contact_id: row.contact_id || null,
    loan_file_id: row.loan_file_id || null,
  };
  if (formId === '92') {
    return {
      ...meta,
      properties: (input.properties || []).map((p) => {
        const years = (p.years || []).map((y) => ({
          tax_year: y.tax_year, months: y.months ?? null,
          lines: Object.fromEntries(Object.entries(y).filter(([k]) => k !== 'tax_year' && k !== 'months')),
        }));
        const pad = [...years];
        while (pad.length < 2) pad.push(newYearSlot((pad[0]?.tax_year || latestYear()) - 1));
        return {
          uid: nextUid(), category: p.category, address: p.address || '', yearCount: years.length || 1,
          monthly_payment: p.monthly_payment ?? null, years: pad,
        };
      }),
    };
  }
  const years = input.years || [];
  const two = years.length === 2;
  const pick = (arr) => (two ? { prior: arr?.[0] ?? null, recent: arr?.[1] ?? null } : { recent: arr?.[0] ?? null });
  return {
    ...meta,
    yearCount: two ? 2 : 1,
    recentYear: years[years.length - 1] || latestYear(),
    method: input.method || null,
    sections: (input.sections || []).map((s) => ({
      uid: nextUid(), type: s.type, name: s.name || '',
      values: Object.fromEntries(Object.entries(s.values || {}).map(([k, arr]) => [k, pick(arr)])),
      ownership: pick(s.ownership_pct),
      owns100: !!s.owns_100_percent,
    })),
  };
}

const filenameFrom = (res, id) => {
  const cd = res?.headers?.['content-disposition'] || '';
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(cd);
  return m ? m[1] : `Form-${id}.pdf`;
};

export function useIncomeForms({ enabled = true } = {}) {
  const [defs, setDefs] = useState(null);
  const [defsError, setDefsError] = useState(null);
  const [tab, setTab] = useState('1084');
  const [forms, setForms] = useState(() => Object.fromEntries(FORM_IDS.map((id) => [id, blankForm(id)])));
  const [worksheets, setWorksheets] = useState([]);
  const formsRef = useRef(forms);
  formsRef.current = forms;
  const defsRef = useRef(defs);
  defsRef.current = defs;

  const patchForm = useCallback((id, patch) => {
    setForms((prev) => ({ ...prev, [id]: { ...prev[id], ...(typeof patch === 'function' ? patch(prev[id]) : patch) } }));
  }, []);

  const refreshList = useCallback(async () => {
    try {
      const { data } = await api.get('/income/forms/worksheets');
      setWorksheets(Array.isArray(data) ? data : (data?.worksheets || data?.items || []));
    } catch (e) {
      toast({ title: 'Saved worksheets did not load', description: errorMessage(e, 'Try again in a moment.') });
    }
  }, []);

  useEffect(() => {
    if (!enabled || defsRef.current) return undefined;
    let live = true;
    api.get('/income/forms/definitions')
      .then(({ data }) => { if (live) setDefs(data.forms); })
      .catch((e) => { if (live) setDefsError(errorMessage(e, 'The form definitions did not load.')); });
    refreshList();
    return () => { live = false; };
  }, [enabled, refreshList]);

  // A contact in the URL pre-fills an empty borrower name on every form.
  useEffect(() => {
    const cid = urlLinks().contact_id;
    if (!enabled || !cid) return;
    uc.get(cid).then((c) => {
      if (!c?.display_name) return;
      setForms((prev) => Object.fromEntries(Object.entries(prev).map(([id, f]) => [
        id,
        f.draft.borrower_name ? f : { ...f, draft: { ...f.draft, borrower_name: c.display_name } },
      ])));
    }).catch(() => {});
  }, [enabled]);

  // Typing into the worksheet: any shown result is stale; a saved worksheet is now dirty.
  const update = useCallback((id, mutate) => {
    patchForm(id, (f) => ({ draft: mutate(f.draft), result: null, dirty: f.worksheet ? true : f.dirty }));
  }, [patchForm]);

  // Name / links: the result does not depend on them.
  const setMeta = useCallback((id, patch) => {
    patchForm(id, (f) => ({ draft: { ...f.draft, ...patch }, dirty: f.worksheet ? true : f.dirty }));
  }, [patchForm]);

  const pickContact = useCallback(async (id, contactId) => {
    setMeta(id, { contact_id: contactId || null, loan_file_id: null });
    if (!contactId || formsRef.current[id].draft.borrower_name) return;
    try {
      const c = await uc.get(contactId);
      if (c?.display_name) {
        patchForm(id, (f) => (f.draft.borrower_name || f.draft.contact_id !== contactId
          ? {} : { draft: { ...f.draft, borrower_name: c.display_name } }));
      }
    } catch { /* the name stays blank; the LO can type it */ }
  }, [setMeta, patchForm]);

  const calculate = useCallback(async (id) => {
    const def = defsRef.current?.[id];
    if (!def) return;
    // The draft object is replaced on every edit, so identity tells us whether the
    // response still describes what is on screen.
    const snapshot = formsRef.current[id].draft;
    patchForm(id, { busy: true, result: null });
    try {
      const { data } = await api.post(`/income/forms/${id}/calculate`, buildPayload(id, def, snapshot));
      patchForm(id, (cur) => (cur.draft === snapshot ? { result: data } : {}));
    } catch (e) {
      toast({ title: 'Calculation failed', description: errorMessage(e, 'The worksheet could not be calculated.') });
    } finally {
      patchForm(id, { busy: false });
    }
  }, [patchForm]);

  const save = useCallback(async (id) => {
    const def = defsRef.current?.[id];
    const f = formsRef.current[id];
    if (!def) return;
    if (!f.draft.contact_id && !f.draft.loan_file_id) {
      toast({ title: 'Pick a contact or a loan', description: 'A worksheet is saved to a contact or a loan.' });
      return;
    }
    patchForm(id, { busy: true });
    const snapshot = f.draft;
    const body = { input: buildPayload(id, def, snapshot), contact_id: f.draft.contact_id || null, loan_file_id: f.draft.loan_file_id || null };
    try {
      const { data } = f.worksheet
        ? await api.put(`/income/forms/worksheets/${f.worksheet.id}`, body)
        : await api.post('/income/forms/worksheets', { form_id: id, ...body });
      // The row exists either way; but if the LO kept typing, the saved copy is already
      // stale: stay dirty (no PDF download) and drop the result.
      patchForm(id, (cur) => {
        const same = cur.draft === snapshot;
        return {
          worksheet: { id: data.worksheet_id, complete: !!data.complete },
          dirty: !same,
          result: same ? (data.result ?? cur.result) : null,
        };
      });
      toast({ title: 'Worksheet saved' });
      refreshList();
    } catch (e) {
      toast({ title: 'Save failed', description: errorMessage(e, 'The worksheet could not be saved.') });
    } finally {
      patchForm(id, { busy: false });
    }
  }, [patchForm, refreshList]);

  const download = useCallback(async (worksheetId) => {
    try {
      const res = await api.get(`/income/forms/worksheets/${worksheetId}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = filenameFrom(res, worksheetId);
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: errorMessage(e, 'The PDF could not be built.') });
    }
  }, []);

  const open = useCallback(async (worksheetId) => {
    try {
      const { data: row } = await api.get(`/income/forms/worksheets/${worksheetId}`);
      const id = row.form_id;
      setForms((prev) => ({
        ...prev,
        [id]: {
          draft: draftFromInput(id, row), result: row.result || null,
          worksheet: { id: row.worksheet_id, complete: !!row.complete }, dirty: false, busy: false,
        },
      }));
      setTab(id);
    } catch (e) {
      toast({ title: 'Could not open the worksheet', description: errorMessage(e, 'Try again in a moment.') });
    }
  }, []);

  const remove = useCallback(async (worksheetId) => {
    if (!(await confirmDialog({ title: 'Delete this worksheet?', confirmText: 'Delete', destructive: true }))) return;
    try {
      await api.delete(`/income/forms/worksheets/${worksheetId}`);
      setForms((prev) => Object.fromEntries(Object.entries(prev).map(([id, f]) => [
        id, f.worksheet?.id === worksheetId ? { ...f, worksheet: null, dirty: false } : f,
      ])));
      refreshList();
    } catch (e) {
      toast({ title: 'Delete failed', description: errorMessage(e, 'The worksheet could not be deleted.') });
    }
  }, [refreshList]);

  return {
    defs, defsError, tab, setTab, forms, worksheets,
    update, setMeta, pickContact, calculate, save, download, open, remove, refreshList,
  };
}
